import { DropdownOption } from 'components/final-form/dropdownUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Accordion, Button, Grid, Icon, Table } from 'semantic-ui-react';
import { Unfit } from 'ts-types/api.types';
import moment, { Moment } from 'moment';
import { Field } from 'react-final-form';
import DatePicker from 'components/final-form/DatePicker';
import Select from 'components/final-form/Select';
import { FieldArray } from 'react-final-form-arrays';
import styled from 'styled-components';
import { composeValidators, isValueValidNumber, mustBeNumber, mustBePositiveNumber } from 'utils/validatorUtils';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import EditActionButton from 'components/EditActionButton';
import SaveActionButton from 'components/SaveActionButton';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import TextArea from 'components/final-form/TextArea';

const StyledBorderGrid = styled(Grid)`
     border-bottom: 1px solid rgba(34,36,38,.1);
`;

const addButton = {
  marginBottom: 10,
  marginTop: 10
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  activeIndex?: number,
  unfit?: Array<Unfit>,
  percents: Array<DropdownOption>,
  readOnly: boolean
}

interface State {
  activeIndex?: number,
  editUnfit: boolean,
  editUnfitIndex?: number,
  unfitCopy?: Partial<Unfit>
  unfit: Array<Partial<Unfit>>,
  errors: Array<boolean>
}

class UnableToWorkSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {activeIndex, unfit = []} = this.props;

    this.state = {
      activeIndex: activeIndex,
      editUnfit: false,
      unfit: unfit,
      errors: [false, false, false]
    }
  }

  validateFields = (unfit: Partial<Unfit>): boolean => {

    let errors: Array<boolean> = [];

    const mustBeNumber = !((unfit.percent || unfit.percent === 0) && isValueValidNumber(unfit.percent));

    errors[0] = unfit.fromDate == "";
    errors[1] = unfit.toDate == "";
    errors[2] = mustBeNumber;

    this.setState({
      errors: errors
    });

    return errors.includes(true);
  };

  addUnfit = (e: any, titleProps: any) => {
    const {index} = titleProps;
    const {activeIndex} = this.state;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({activeIndex: newIndex})
  };

  confirmAddUnfit = (fields: any, index: number) => {

    const {options: {workDate}} = this.props;
    const {unfit} = this.state;

    let isFirstUnfitEntry = !(unfit && unfit.length > 0);

    let newFromDate : Moment = moment(workDate);
    if (index > 0) {
      const lastEntryEndDate = fields.value[index - 1].toDate;
      newFromDate = moment(lastEntryEndDate).add(1, 'days');
    }
    const fromDate = newFromDate.format("YYYY-MM-DD");

    // add 3 days (including today) for the first entry; 2 days for the following entries
    let daysToAdd = isFirstUnfitEntry ? 2 : 1;
    if (newFromDate.isoWeekday() == 7) {
      // if Sunday => don't count today
      daysToAdd += 1;
    }
    const toDate = newFromDate.add(daysToAdd, 'days').format("YYYY-MM-DD");

    const newUnfit: Partial<Unfit> = {
      fromDate: fromDate,
      toDate: toDate,
      percent: 100
    };

    fields.push(newUnfit);

    this.setState({
      editUnfit: true,
      editUnfitIndex: index,
      unfit: unfit ? [...unfit, newUnfit] : [newUnfit]
    })
  };

  deleteUnfitItem = (fields: any, index: number) => {

    fields.remove(index);

    const {unfit} = this.state;
    this.setState({
      unfit: unfit.filter((item, ix) => ix != index)
    });
  };

  saveUnfit = (unfit: Partial<Unfit>) => {
    if (!this.validateFields(unfit)) {
      this.setState({
        editUnfit: false,
        editUnfitIndex: undefined
      });
    }
  };

  cancelEditUnfit = (fields: any, index: number) => {

    const {unfitCopy} = this.state;

    this.setState({
      editUnfit: false,
      editUnfitIndex: undefined,
      unfitCopy: undefined,
      errors: [false, false, false]
    });

    fields.remove(index);
    if (unfitCopy) {
      fields.insert(index, unfitCopy);
    }
  };

  editUnfit = (index: number, field: Partial<Unfit>, fields: any): void => {

    const {editUnfit, editUnfitIndex, unfitCopy} = this.state;

    if (editUnfit) {

      fields.remove(editUnfitIndex);
      fields.insert(editUnfitIndex, unfitCopy);

      this.setState({
        editUnfitIndex: index,
        unfitCopy: field
      });
    } else {
      this.setState({
        editUnfit: true,
        editUnfitIndex: index,
        unfitCopy: field
      })
    }
  };

  render(): React.ReactNode {

    const {getMessage, readOnly} = this.props;
    const {activeIndex, editUnfit, editUnfitIndex} = this.state;

    return (
        <StyledBorderGrid>
          <Accordion fluid>
            <Accordion.Title active={activeIndex === 0} index={0} onClick={this.addUnfit}>
              <h4><Icon name='dropdown'/>{getMessage('unfit.titleHeader')}</h4>
            </Accordion.Title>
            <Accordion.Content active={activeIndex === 0} style={{paddingBottom: 20}}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column>
                    <Table celled selectable>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>{getMessage('from')}</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('to')}</Table.HeaderCell>
                          <Table.HeaderCell>%</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('actions')}</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        <FieldArray name="unfit">
                          {({fields}) =>
                              fields.map((field, index: number) => {
                                if (fields.value[index] !== undefined) {
                                  return (
                                      (editUnfit && editUnfitIndex === index) ?
                                          this.renderUnfitForm(field, fields.value[index], index, fields) :
                                          this.renderUnfitTable(fields.value[index], index, fields)
                                  )
                                }
                              })
                          }
                        </FieldArray>
                      </Table.Body>
                    </Table>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <FieldArray name="unfit">
                      {({fields}) => (
                          <Button
                              type="button"
                              compact
                              style={addButton}
                              onClick={() => this.confirmAddUnfit(fields, fields.length!)}
                              disabled={editUnfit || readOnly}
                          >
                            {getMessage('button.add')}
                          </Button>
                      )}
                    </FieldArray>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Field
                        name="unfitRemark"
                        component={TextArea}
                        rows={2}
                        placeholder={getMessage("remarks")}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Accordion.Content>
          </Accordion>
        </StyledBorderGrid>
    );
  }

  renderUnfitTable = (unfit: Partial<Unfit>, index: number, fields: any) => {
    const {readOnly, getMessage} = this.props;
    return (
        <React.Fragment key={index}>
          <Table.Row>
            <Table.Cell width={5}>
              {moment(unfit.fromDate).format("DD.MM.YYYY")}
            </Table.Cell>
            <Table.Cell width={5}>
              {moment(unfit.toDate).format("DD.MM.YYYY")}
            </Table.Cell>
            <Table.Cell width={4}>{unfit.percent}%</Table.Cell>
            <Table.Cell width={2}>
              <EditActionButton
                  hoverMessage={getMessage('edit')}
                  onConfirm={() => this.editUnfit(index, unfit, fields)}
              />
              <DeleteActionButton
                  hoverMessage={getMessage("msgDelete")}
                  onConfirm={() => this.deleteUnfitItem(fields, index)}
                  disabled={readOnly}
              />
            </Table.Cell>
          </Table.Row>
        </React.Fragment>
    );
  };

  renderUnfitForm = (field: string, unfit: Partial<Unfit>, index: number, fields: any) => {

    const {percents, getMessage} = this.props;
    const {unfitCopy, errors} = this.state;

    if (unfitCopy && unfitCopy.percent) {
      const percentage = {
        key: unfitCopy.percent,
        text: unfitCopy.percent,
        value: unfitCopy.percent
      };

      const isMember = (percent: any) => {
        return percent.value === percentage.value;
      };

      if (!percents.find(isMember)) {
        percents.push(percentage);
      }
      percents.sort(function (a, b) {
        return a.value - b.value
      });
    }

    const disabledBefore = unfit.fromDate && new Date(unfit.fromDate);

    return (
        <Table.Row key={index}>
          <Table.Cell>
            <Field
                name={`${field}.fromDate`}
                component={DatePicker}
                meta={{touched: errors[0], error: errors[0]}}
            />
          </Table.Cell>
          <Table.Cell>
            <Field
                name={`${field}.toDate`}
                component={DatePicker}
                disabledBefore={disabledBefore}
                meta={{touched: errors[1], error: errors[1]}}
            />
          </Table.Cell>
          <Table.Cell>
            <Field
                name={`${field}.percent`}
                component={Select}
                options={percents}
                error={errors[2]}
                search
                selection
                allowAdditions
                upward
                onKeyDown={ensureOnlyNumericInput}
                validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
            />
          </Table.Cell>
          <Table.Cell>
            <SaveActionButton hoverMessage={getMessage('button.save')}
                              onConfirm={() => this.saveUnfit(unfit)}/>
            <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                onConfirm={() => this.cancelEditUnfit(fields, index)}/>
          </Table.Cell>
        </Table.Row>
    );
  }
}

export default withAuthContext(withMessages(UnableToWorkSegment));
